import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  FileDown,
  Lock,
  Unlock,
  Wallet,
} from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { SocioAutocomplete } from "../../components/SocioAutocomplete";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  PageHeader,
  TableWrap,
  Textarea,
} from "../../components/ui";
import { getAuth, hasPermission } from "../../lib/auth";
import { hoyLocalISO } from "../../lib/fechaLocal";
import type { OrigenNavegacion as Desde } from "../../components/ui";
import { listarUsuarios } from "../usuarios/api";
import {
  cerrarCaja,
  descargarCajaCsv,
  listarCierresCaja,
  obtenerArqueo,
  reabrirCaja,
  FORMAS_CON_DINERO,
  type CierreCaja,
} from "./api";

const hoyISO = hoyLocalISO;

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fechaLarga(iso: string): string {
  // `iso` es YYYY-MM-DD: parsearlo directo evita el corrimiento de zona.
  const [a, m, d] = iso.split("-").map(Number);
  if (!a || !m || !d) return iso;
  return new Date(a, m - 1, d).toLocaleDateString("es-CL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Caja del día (recorrido F4).
 *
 * La tesorera cierra el día contando el cajón: esta pantalla le dice cuánto
 * entró según el sistema, por qué forma de pago y quién lo recibió, y le deja
 * declarar lo que contó para que la diferencia quede registrada.
 */
export function CajaPage(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const auth = getAuth();
  const puedeCerrar = hasPermission(auth, "caja.cerrar");
  const puedeRegistrar = hasPermission(auth, "pagos.registrar");
  const navigate = useNavigate();
  const location = useLocation();

  // Día y recaudador en la URL: al volver desde la ficha de un socio la caja
  // queda en el mismo día (convención `desde`, auditoría NAV-07).
  const [params, setParams] = useSearchParams();
  const fecha = params.get("fecha") || hoyISO();
  const recaudador = params.get("recibido_por") ?? "";
  function setFiltro(clave: string, valor: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (valor) next.set(clave, valor);
        else next.delete(clave);
        return next;
      },
      { replace: true },
    );
  }
  const setFecha = (f: string): void => setFiltro("fecha", f === hoyISO() ? "" : f);
  const setRecaudador = (r: string): void => setFiltro("recibido_por", r);
  const desde: Desde = {
    etiqueta: "Caja del día",
    ruta: `${location.pathname}${location.search}`,
  };
  const [buscandoSocio, setBuscandoSocio] = useState(false);
  function irARegistrarPago(socioId: number): void {
    setBuscandoSocio(false);
    navigate(`/socios/${socioId}/cuenta-corriente?accion=pagar`, { state: { desde } });
  }

  const [declarado, setDeclarado] = useState<Record<string, string>>({});
  const [observaciones, setObservaciones] = useState("");
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const [reabriendo, setReabriendo] = useState<CierreCaja | null>(null);
  const [descargando, setDescargando] = useState(false);

  const arqueoQ = useQuery({
    queryKey: ["caja", fecha, recaudador || null],
    queryFn: () => obtenerArqueo(fecha, recaudador ? Number(recaudador) : null),
  });
  const usuariosQ = useQuery({ queryKey: ["usuarios"], queryFn: () => listarUsuarios() });
  const cierresQ = useQuery({ queryKey: ["caja-cierres"], queryFn: listarCierresCaja });

  const arqueo = arqueoQ.data;

  // Al cambiar de día se arranca de cero: lo declarado es de ese día.
  useEffect(() => {
    setDeclarado({});
    setObservaciones("");
  }, [fecha]);

  const sistemaPorForma = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of arqueo?.por_forma ?? []) m.set(t.clave, t.monto);
    return m;
  }, [arqueo]);

  const totalDeclarado = useMemo(
    () =>
      FORMAS_CON_DINERO.reduce((acc, f) => acc + (Number(declarado[f]) || 0), 0),
    [declarado],
  );
  const diferencia = totalDeclarado - (arqueo?.total_recaudado ?? 0);
  // Un campo que se escribió y se borró no cuenta como declarado: cerrar
  // sin haber contado nada firmaba un arqueo vacío (auditoría UX-08).
  const hayDeclaracion = FORMAS_CON_DINERO.some(
    (f) => (declarado[f] ?? "").trim() !== "",
  );

  const cerrarM = useMutation({
    mutationFn: () =>
      cerrarCaja({
        fecha,
        declarado: Object.fromEntries(
          FORMAS_CON_DINERO.map((f) => [f, Number(declarado[f]) || 0]),
        ),
        observaciones: observaciones.trim() || null,
      }),
    onSuccess: (c) => {
      toast.success(
        c.diferencia === 0
          ? "Caja cerrada: cuadra."
          : `Caja cerrada con diferencia de ${formatCLP(c.diferencia)}.`,
      );
      setConfirmarCierre(false);
      void qc.invalidateQueries({ queryKey: ["caja"] });
      void qc.invalidateQueries({ queryKey: ["caja-cierres"] });
    },
    onError: (err: unknown) => {
      setConfirmarCierre(false);
      toast.error(err instanceof ApiError ? err.message : "No se pudo cerrar la caja");
    },
  });

  const reabrirM = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) =>
      reabrirCaja(id, motivo),
    onSuccess: () => {
      toast.success("Caja reabierta. Queda registrado el motivo.");
      setReabriendo(null);
      void qc.invalidateQueries({ queryKey: ["caja"] });
      void qc.invalidateQueries({ queryKey: ["caja-cierres"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo reabrir la caja"),
  });

  async function descargar(): Promise<void> {
    setDescargando(true);
    try {
      await descargarCajaCsv(fecha, recaudador ? Number(recaudador) : null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "No se pudo exportar");
    } finally {
      setDescargando(false);
    }
  }

  const cierre = arqueo?.cierre ?? null;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Caja del día"
        subtitle="Arqueo de los pagos recibidos en el día y cierre de caja."
        icon={Wallet}
        actions={
          <>
          <Button
            variant="secondary"
            size="sm"
            icon={FileDown}
            disabled={descargando || !arqueo || arqueo.pagos.length === 0}
            onClick={() => void descargar()}
          >
            {descargando ? "Generando…" : "Exportar CSV"}
          </Button>
          {puedeRegistrar && (
            <Button icon={CreditCard} onClick={() => setBuscandoSocio(true)}>
              Registrar pago
            </Button>
          )}
          </>
        }
      />

      <Modal
        open={buscandoSocio}
        onClose={() => setBuscandoSocio(false)}
        title="Registrar pago"
        subtitle="Busca al socio: se abre su cuenta corriente con el formulario de pago listo."
        icon={CreditCard}
        size="md"
      >
        <div className="min-h-[18rem]">
          <SocioAutocomplete
            autoFocus
            label="Socio (nombre, RUT o N° de socio)"
            onSelect={(s) => irARegistrarPago(s.id)}
          />
        </div>
      </Modal>

      {/* Filtros */}
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700">Día</span>
            <Input
              type="date"
              value={fecha}
              max={hoyISO()}
              onChange={(e) => setFecha(e.target.value)}
              className="mt-1"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700">
              Recibido por
            </span>
            <select
              value={recaudador}
              onChange={(e) => setRecaudador(e.target.value)}
              className="mt-1 rounded-md border border-neutral-450 px-3 py-2 text-sm shadow-sm"
            >
              <option value="">Todos</option>
              {(usuariosQ.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-neutral-500 pb-2">{fechaLarga(fecha)}</p>
        </div>
      </Card>

      {arqueoQ.isPending && <Cargando />}
      {arqueoQ.isError && (
        <ErrorCarga
          error={arqueoQ.error}
          mensaje="No se pudo cargar la caja del día."
          onReintentar={() => void arqueoQ.refetch()}
        />
      )}

      {arqueo && (
        <>
          {/* Estado del día */}
          {cierre && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                cierre.diferencia === 0
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-amber-200 bg-amber-50 text-amber-900"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Lock className="h-4 w-4" strokeWidth={1.5} />
                <strong>Caja cerrada</strong>
                {cierre.cerrado_por_nombre && <>· por {cierre.cerrado_por_nombre}</>}
                <span className="ml-auto flex items-center gap-2">
                  Sistema {formatCLP(cierre.total_sistema)} · Declarado{" "}
                  {formatCLP(cierre.total_declarado)} ·{" "}
                  <strong>
                    {cierre.diferencia === 0
                      ? "cuadra"
                      : `diferencia ${formatCLP(cierre.diferencia)}`}
                  </strong>
                  {puedeCerrar && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Unlock}
                      onClick={() => setReabriendo(cierre)}
                    >
                      Reabrir
                    </Button>
                  )}
                </span>
              </div>
              {cierre.observaciones && (
                <p className="mt-1 text-xs">{cierre.observaciones}</p>
              )}
              <p className="mt-1 text-xs opacity-80">
                Mientras esté cerrada no se pueden registrar ni anular pagos con
                esta fecha.
              </p>
            </div>
          )}

          {/* Totales */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tarjeta
              label="Recaudado"
              valor={formatCLP(arqueo.total_recaudado)}
              tono="ok"
            />
            <Tarjeta label="Pagos" valor={String(arqueo.cantidad_pagos)} />
            <Tarjeta
              label="Anulados"
              valor={`${arqueo.anulados_cantidad} · ${formatCLP(arqueo.anulados_monto)}`}
              tono={arqueo.anulados_cantidad > 0 ? "warn" : "muted"}
            />
            <Tarjeta
              label="Saldo a favor aplicado"
              valor={String(arqueo.aplicaciones_saldo_favor)}
              tono="muted"
              ayuda="Créditos imputados a boletas: no entra dinero al cajón."
            />
          </section>

          {/* Cuadre */}
          <section className="bg-white rounded-lg border border-neutral-200">
            <header className="px-4 py-3 border-b border-neutral-100">
              <h3 className="text-sm font-semibold text-neutral-900">Cuadre</h3>
              <p className="text-xs text-neutral-600 mt-0.5">
                Anota lo que contaste por forma de pago. La diferencia se calcula
                sola y queda registrada al cerrar.
              </p>
            </header>
            <div className="p-4 space-y-3">
              <TableWrap label="Cuadre por forma de pago">
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">Forma de pago</th>
                    <th scope="col" className="px-3 py-2 text-right">Pagos</th>
                    <th scope="col" className="px-3 py-2 text-right">Sistema</th>
                    <th scope="col" className="px-3 py-2 text-right">Contado</th>
                    <th scope="col" className="px-3 py-2 text-right">Diferencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {FORMAS_CON_DINERO.map((forma) => {
                    const sistema = sistemaPorForma.get(forma) ?? 0;
                    const fila = arqueo.por_forma.find((t) => t.clave === forma);
                    const contado = Number(declarado[forma]) || 0;
                    const dif = contado - sistema;
                    const declaradoEnCierre =
                      cierre?.detalle?.[forma]?.declarado ?? null;
                    return (
                      <tr key={forma} className={sistema === 0 ? "text-neutral-500" : ""}>
                        <td className="px-3 py-2 font-medium">{forma}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {fila?.cantidad ?? 0}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatCLP(sistema)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {cierre ? (
                            <span className="tabular-nums">
                              {declaradoEnCierre != null
                                ? formatCLP(declaradoEnCierre)
                                : "—"}
                            </span>
                          ) : (
                            <Input
                              type="number"
                              min={0}
                              inputMode="numeric"
                              value={declarado[forma] ?? ""}
                              placeholder="0"
                              onChange={(e) =>
                                setDeclarado((prev) => ({
                                  ...prev,
                                  [forma]: e.target.value,
                                }))
                              }
                              className="w-32 text-right"
                            />
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-right tabular-nums ${
                            !cierre && hayDeclaracion && dif !== 0
                              ? "text-rose-700 font-medium"
                              : "text-neutral-500"
                          }`}
                        >
                          {cierre
                            ? declaradoEnCierre != null
                              ? formatCLP(declaradoEnCierre - sistema)
                              : "—"
                            : hayDeclaracion
                            ? formatCLP(dif)
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-neutral-50">
                  <tr>
                    <td className="px-3 py-2 font-semibold">Total</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {arqueo.cantidad_pagos}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {formatCLP(arqueo.total_recaudado)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {cierre
                        ? formatCLP(cierre.total_declarado)
                        : hayDeclaracion
                        ? formatCLP(totalDeclarado)
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {cierre ? (
                        <DiferenciaBadge valor={cierre.diferencia} />
                      ) : hayDeclaracion ? (
                        <DiferenciaBadge valor={diferencia} />
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
              </TableWrap>

              {!cierre && puedeCerrar && (
                <div className="flex flex-wrap items-end gap-3 pt-2">
                  <label className="flex-1 min-w-[260px]">
                    <span className="block text-xs font-medium text-neutral-700">
                      Observaciones del cierre (opcional)
                    </span>
                    <Textarea
                      rows={2}
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      placeholder="Ej: faltan $20, se revisa mañana"
                      className="mt-1"
                    />
                  </label>
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      variant="primary"
                      icon={Lock}
                      disabled={cerrarM.isPending || !hayDeclaracion}
                      onClick={() => setConfirmarCierre(true)}
                      aria-describedby={hayDeclaracion ? undefined : "caja-cerrar-ayuda"}
                    >
                      {cerrarM.isPending ? "Cerrando…" : "Cerrar caja del día"}
                    </Button>
                    {!hayDeclaracion && (
                      <p id="caja-cerrar-ayuda" className="text-xs text-neutral-600">
                        Anota primero lo que contaste en &ldquo;Contado&rdquo; (aunque
                        sea $0) para poder cerrar.
                      </p>
                    )}
                  </div>
                </div>
              )}
              {!cierre && !puedeCerrar && (
                <p className="text-xs text-neutral-500">
                  Cerrar la caja requiere el permiso &ldquo;Cerrar y reabrir la caja
                  del día&rdquo;: quien recauda no firma su propio arqueo.
                </p>
              )}
            </div>
          </section>

          {/* Por recaudador */}
          {arqueo.por_recaudador.length > 1 && (
            <section className="bg-white rounded-lg border border-neutral-200 p-4">
              <h3 className="text-sm font-semibold text-neutral-900 mb-2">
                Por recaudador
              </h3>
              <div className="flex flex-wrap gap-2">
                {arqueo.por_recaudador.map((t) => (
                  <div
                    key={t.clave || "sin"}
                    className="rounded-md border border-neutral-200 px-3 py-2 text-sm"
                  >
                    <div className="font-medium text-neutral-900">{t.etiqueta}</div>
                    <div className="text-xs text-neutral-500">
                      {t.cantidad} pago(s) · {formatCLP(t.monto)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Detalle */}
          <Card padding="none" className="overflow-hidden">
            <header className="px-4 py-3 border-b border-neutral-100">
              <h3 className="text-sm font-semibold text-neutral-900">
                Pagos del día ({arqueo.pagos.length})
              </h3>
            </header>
            {arqueo.pagos.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon={Wallet}
                  tone="slate"
                  title="Sin pagos ese día"
                  description={
                    puedeRegistrar
                      ? "Cuando un socio pague en el mesón, regístralo acá. También puedes elegir otro día."
                      : "Elige otro día para ver sus pagos."
                  }
                >
                  {puedeRegistrar && (
                    <Button icon={CreditCard} onClick={() => setBuscandoSocio(true)}>
                      Registrar pago
                    </Button>
                  )}
                </EmptyState>
              </div>
            ) : (
              <>
              {/* Móvil: tarjetas (auditoría A11Y-01). */}
              <ul className="divide-y divide-neutral-100 md:hidden">
                {arqueo.pagos.map((p) => (
                  <li
                    key={p.id}
                    className={`px-4 py-3 text-sm ${
                      p.estado === "Anulado" ? "bg-neutral-50 text-neutral-500" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Link
                          to={`/socios/${p.socio_id}/cuenta-corriente`}
                          state={{ desde }}
                          className="font-medium text-neutral-900 hover:text-brand-700"
                        >
                          {p.socio_nombre}
                        </Link>
                        <div className="text-xs text-neutral-500">
                          {hora(p.fecha_pago)} · {p.forma_pago}
                          {p.recibido_por_nombre ? ` · ${p.recibido_por_nombre}` : ""}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium tabular-nums">
                          {formatCLP(p.monto_pagado)}
                        </div>
                        {p.estado === "Anulado" && <Badge tone="slate">Anulado</Badge>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <TableWrap label="Pagos del día" className="hidden rounded-none ring-0 md:block">
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">Hora</th>
                    <th scope="col" className="px-3 py-2 text-left">Socio</th>
                    <th scope="col" className="px-3 py-2 text-left">Forma</th>
                    <th scope="col" className="px-3 py-2 text-right">Monto</th>
                    <th scope="col" className="px-3 py-2 text-left">Comprobante</th>
                    <th scope="col" className="px-3 py-2 text-left">Recibido por</th>
                    <th scope="col" className="px-3 py-2 text-left">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {arqueo.pagos.map((p) => (
                    <tr
                      key={p.id}
                      className={p.estado === "Anulado" ? "bg-neutral-50 text-neutral-500" : ""}
                    >
                      <td className="px-3 py-2 tabular-nums">{hora(p.fecha_pago)}</td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/socios/${p.socio_id}/cuenta-corriente`}
                          state={{ desde }}
                          className="font-medium text-neutral-900 hover:text-brand-700"
                        >
                          {p.socio_nombre}
                        </Link>
                        <div className="text-xs text-neutral-500">{p.socio_rut ?? ""}</div>
                      </td>
                      <td className="px-3 py-2">{p.forma_pago}</td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">
                        {formatCLP(p.monto_pagado)}
                      </td>
                      <td className="px-3 py-2 text-neutral-600 font-mono text-xs">
                        {p.nro_comprobante ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-neutral-600">
                        {p.recibido_por_nombre ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        {p.estado === "Anulado" ? (
                          <Badge tone="slate">Anulado</Badge>
                        ) : (
                          <Badge tone="emerald">
                            <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
                            Activo
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </TableWrap>
              </>
            )}
          </Card>

          {/* Historial */}
          {(cierresQ.data ?? []).length > 0 && (
            <section className="bg-white rounded-lg border border-neutral-200 p-4">
              <h3 className="text-sm font-semibold text-neutral-900 mb-2">
                Últimos arqueos
              </h3>
              <div className="flex flex-wrap gap-2">
                {(cierresQ.data ?? []).slice(0, 12).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setFecha(c.fecha)}
                    className="rounded-md border border-neutral-200 px-3 py-1.5 text-left text-xs hover:bg-neutral-50"
                  >
                    <div className="font-medium text-neutral-900">{c.fecha}</div>
                    <div className="text-neutral-500">
                      {c.estado === "Reabierta" ? "reabierta · " : ""}
                      {c.diferencia === 0
                        ? "cuadró"
                        : `dif. ${formatCLP(c.diferencia)}`}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmarCierre}
        tone="default"
        title="Cerrar la caja del día"
        confirmLabel="Cerrar caja"
        loading={cerrarM.isPending}
        description={
          <>
            El sistema registró {formatCLP(arqueo?.total_recaudado ?? 0)} y tú
            contaste {formatCLP(totalDeclarado)}
            {diferencia !== 0 ? (
              <>
                : queda una diferencia de <strong>{formatCLP(diferencia)}</strong>,
                que va a quedar registrada.
              </>
            ) : (
              <>: la caja cuadra.</>
            )}{" "}
            Después de cerrar, este día no acepta pagos ni anulaciones hasta que se
            reabra.
          </>
        }
        onConfirm={() => cerrarM.mutate()}
        onCancel={() => setConfirmarCierre(false)}
      />

      {reabriendo && (
        <ReabrirModal
          cierre={reabriendo}
          pendiente={reabrirM.isPending}
          onCancel={() => setReabriendo(null)}
          onConfirm={(motivo) => reabrirM.mutate({ id: reabriendo.id, motivo })}
        />
      )}
    </div>
  );
}

function DiferenciaBadge({ valor }: { valor: number }): JSX.Element {
  if (valor === 0) {
    return (
      <Badge tone="emerald">
        <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
        Cuadra
      </Badge>
    );
  }
  return (
    <Badge tone={valor > 0 ? "amber" : "rose"}>
      <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />
      {valor > 0 ? "Sobra " : "Falta "}
      {formatCLP(Math.abs(valor))}
    </Badge>
  );
}

function ReabrirModal({
  cierre,
  pendiente,
  onCancel,
  onConfirm,
}: {
  cierre: CierreCaja;
  pendiente: boolean;
  onCancel: () => void;
  onConfirm: (motivo: string) => void;
}): JSX.Element {
  const [motivo, setMotivo] = useState("");
  return (
    <Modal
      open
      onClose={onCancel}
      title={`Reabrir la caja del ${cierre.fecha}`}
      icon={Unlock}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={pendiente}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={pendiente || motivo.trim().length < 3}
            onClick={() => onConfirm(motivo.trim())}
          >
            {pendiente ? "Reabriendo…" : "Reabrir caja"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-600">
          El arqueo firmado ({formatCLP(cierre.total_declarado)} declarados,
          diferencia {formatCLP(cierre.diferencia)}) queda guardado como historia,
          y el día vuelve a aceptar pagos y anulaciones.
        </p>
        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">
            Motivo de la reapertura
          </span>
          <Textarea
            rows={2}
            value={motivo}
            autoFocus
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: se cargó un pago al socio equivocado"
            className="mt-1"
          />
        </label>
      </div>
    </Modal>
  );
}

function Tarjeta({
  label,
  valor,
  tono = "info",
  ayuda,
}: {
  label: string;
  valor: string;
  tono?: "info" | "ok" | "warn" | "muted";
  ayuda?: string;
}): JSX.Element {
  const map: Record<string, string> = {
    info: "bg-white border-neutral-200 text-neutral-900",
    ok: "bg-emerald-50 border-emerald-100 text-emerald-900",
    warn: "bg-amber-50 border-amber-100 text-amber-900",
    muted: "bg-neutral-50 border-neutral-100 text-neutral-700",
  };
  return (
    <div className={`rounded-lg border p-3 ${map[tono]}`} title={ayuda}>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 text-xl font-semibold">{valor}</div>
    </div>
  );
}
